import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { ErrorBoundary, type ErrorBoundaryFallbackProps } from '@suspensive/react';
import { QueryErrorResetBoundary, useQueryClient } from '@tanstack/react-query';
import { usePathname, useRouter } from 'next/navigation';
import { type PropsWithChildren, Suspense } from 'react';
import { useDispatch } from 'react-redux';

import ProductLoading from '@/app/components/ProductLoading';
import { authActions } from '@/stores/authSlice';
import { historyActions } from '@/stores/historySlice';

export const ErrorBoundaryFallback = (props: ErrorBoundaryFallbackProps) => {
  const dispatch = useDispatch();
  const pathname = usePathname();

  const queryClient = useQueryClient();
  const router = useRouter();

  console.error({ props });

  const handleLogout = (nextUrl?: string): void => {
    dispatch(authActions.clearUser());
    dispatch(historyActions.clearHistory());
    queryClient.clear();
    router.push(nextUrl ? `/login/?nextUrl=${nextUrl}` : '/login');
  };

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        background: 'white',
      }}
    >
      <Box sx={{ height: 'calc(40% - 160px)' }}></Box>
      <Box
        sx={{
          height: '260px',
          width: '260px',
          borderRadius: '50%',
          background: '#c0c6cc',
          margin: 'auto',
        }}
      >
        <Typography
          variant="body1"
          sx={{ lineHeight: '260px', fontSize: '5rem', color: '#FFF', fontWeight: 700, textAlign: 'center' }}
        >
          {props.error.name}
        </Typography>
      </Box>
      <Typography
        variant="body1"
        sx={{
          lineHeight: '40px',
          fontSize: '26px',
          color: '#414b55',
          marginTop: '24px',
          textAlign: 'center',
        }}
      >
        There is an error while processing this request. <br /> Please{' '}
        <Button variant="text" sx={{ lineHeight: '40px', fontSize: '26px' }} onClick={props.reset}>
          Try Again
        </Button>
        <Box component="span" my={2}>
          or
        </Box>
        <Button
          sx={{
            lineHeight: '40px',
            fontSize: '26px',
            margin: 'auto',
          }}
          onClick={() => handleLogout(pathname.slice(1))}
        >
          Logout
        </Button>
      </Typography>
    </Box>
  );
};

type Props = {
  loadingType?: 'page' | 'dialog';
};

const ComponentWithQueryErrorBoundary = ({ children }: PropsWithChildren<Props>) => {
  return (
    <QueryErrorResetBoundary>
      {() => (
        <ErrorBoundary fallback={ErrorBoundaryFallback}>
          <Suspense fallback={<ProductLoading />}>{children}</Suspense>
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  );
};

export default ComponentWithQueryErrorBoundary;
